import { createContext, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from "react";
import type { ApiClient } from "./api";
import type { Me } from "./types";
export type Language = "en" | "es";
export interface LanguageSettings { enabled: boolean; languages: string[]; default_language: "browser" | Language }
const messages: Record<string, string> = {};
let currentLanguage: Language = "en";
const listeners = new Set<() => void>();
export function registerMessages(catalog: Record<string, string>) { Object.assign(messages, catalog); }
export function translate(message: string, params: Record<string, string | number> = {}): string {
  const text = currentLanguage === "es" ? messages[message] ?? message : message;
  return text.replace(/\{(\w+)\}/g, (match, key: string) => params[key] === undefined ? match : String(params[key]));
}
export function resolveLanguage(settings: LanguageSettings | undefined, preference?: string | null, browser = typeof navigator === "undefined" ? "en" : navigator.language): Language {
  if (!settings?.enabled) return "en";
  const candidate = preference || (settings.default_language === "browser" ? browser.split("-")[0] : settings.default_language);
  return candidate === "es" && settings.languages.includes("es") ? "es" : "en";
}
export function getDisplayLanguage(): Language { return currentLanguage; }
export function setDisplayLanguage(language: Language) {
  currentLanguage = language;
  if (typeof document !== "undefined") document.documentElement.lang = language;
  listeners.forEach(fn => fn());
}
export function formatNumber(value: number, options?: Intl.NumberFormatOptions) { return new Intl.NumberFormat(currentLanguage === "es" ? "es-419" : "en-US", options).format(value); }
export function formatDate(value: string | number | Date, options?: Intl.DateTimeFormatOptions) { return new Intl.DateTimeFormat(currentLanguage === "es" ? "es-419" : "en-US", options).format(new Date(value)); }
const LanguageContext = createContext<{ enabled: boolean; setLanguage: (language: Language | null) => Promise<void> }>({enabled:false,setLanguage:async()=>{}});
export function LanguageProvider({api, me, refreshMe, children}: {api: ApiClient; me: Me; refreshMe: () => Promise<void>; children: ReactNode}) {
  const effective = resolveLanguage(me.language, me.user.preferred_language);
  // Resolve before descendants render so standalone formatters share the same locale.
  currentLanguage = effective;
  useEffect(() => { setDisplayLanguage(effective); }, [effective]);
  const value = useMemo(() => ({enabled: me.language?.enabled === true, setLanguage: async (language: Language | null) => {
    await api.patch("/me/profile", {preferred_language: language});
    await refreshMe();
  }}), [api, refreshMe, me.language?.enabled]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  const language = useSyncExternalStore(fn => { listeners.add(fn); return () => listeners.delete(fn); }, () => currentLanguage, () => "en" as Language);
  return {...context, language, t: translate};
}
export function LanguageSelector() {
  const {enabled, language, setLanguage, t} = useLanguage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  if (!enabled) return null;
  return <label style={{display:"grid",gap:4}}>{t("Language")}<select className="input" aria-label={t("Language")} value={language} disabled={busy} onChange={async e => {
    setBusy(true); setError(false);
    try { await setLanguage(e.target.value as Language); } catch { setError(true); } finally { setBusy(false); }
  }}><option value="en">English</option><option value="es">Español</option></select>{error && <span role="alert">{t("Could not save language. Try again.")}</span>}</label>;
}
